import React, { useState, useEffect, useMemo } from 'react';
import MobileCameraUpload from './MobileCameraUpload';
import DesktopQRUpload from './DesktopQRUpload';

interface ExtractedProduct {
  name: string;
  brand?: string | null;
  category?: string | null;
  confidence: number;
  quantity?: number | null;
}

interface UploadedImage {
  s3_key: string;
  url: string;
  extracted_products: ExtractedProduct[];
  extraction_status: 'pending' | 'processing' | 'completed' | 'failed';
  image_quality?: string | null;
  upload_timestamp: string;
  localPreview?: string;
}

interface PhotoUploadResponse {
  images: UploadedImage[];
  all_extracted_products: ExtractedProduct[];
  text_fallback?: string;
}

interface PhotoUploadQuestionProps {
  questionId: string;
  surveyId: number;
  sessionId: number;
  questionText?: string;
  maxImages?: number;
  onComplete: (response: PhotoUploadResponse) => void;
  disabled?: boolean;
}

const PhotoUploadQuestion: React.FC<PhotoUploadQuestionProps> = ({
  questionId,
  surveyId,
  sessionId,
  questionText = '',
  maxImages = 3,
  onComplete,
  disabled = false
}) => {
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [textFallback, setTextFallback] = useState<string>('');
  const [deviceType, setDeviceType] = useState<'mobile' | 'desktop'>('desktop');

  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isSmallScreen = window.innerWidth < 768;
    setDeviceType(isMobile || isSmallScreen ? 'mobile' : 'desktop');
  }, []);

  const buildResponse = (
    nextImages: UploadedImage[],
    nextTextFallback: string
  ): PhotoUploadResponse => {
    const allProducts: ExtractedProduct[] = [];
    const seen = new Set<string>();

    for (const img of nextImages) {
      for (const product of img.extracted_products || []) {
        const key = `${product.name.toLowerCase()}-${(product.brand || '').toLowerCase()}`;
        if (!seen.has(key)) {
          seen.add(key);
          allProducts.push(product);
        }
      }
    }

    const response: PhotoUploadResponse = {
      images: nextImages.map(img => ({
        s3_key: img.s3_key,
        url: img.url,
        extracted_products: img.extracted_products,
        extraction_status: img.extraction_status,
        image_quality: img.image_quality,
        upload_timestamp: img.upload_timestamp
      })),
      all_extracted_products: allProducts
    };

    if (nextTextFallback && nextTextFallback.trim().length > 0) {
      response.text_fallback = nextTextFallback.trim();
    }

    return response;
  };

  const handleImagesChange = (newImages: UploadedImage[]) => {
    setImages(newImages);
    onComplete(buildResponse(newImages, textFallback));
  };

  const handleTextFallbackChange = (text: string) => {
    setTextFallback(text);
    onComplete(buildResponse(images, text));
  };

  const hasCompletedAnalysis = useMemo(() => {
    return (
      images.some(img => img.extraction_status === 'completed') ||
      textFallback.trim().length > 0
    );
  }, [images, textFallback]);

  if (deviceType === 'mobile') {
    return (
      <MobileCameraUpload
        sessionId={sessionId}
        questionId={questionId}
        questionText={questionText}
        maxImages={maxImages}
        onImagesChange={handleImagesChange}
        images={images}
        disabled={disabled}
        onTextFallback={handleTextFallbackChange}
        textFallback={textFallback}
      />
    );
  }

  return (
    <DesktopQRUpload
      surveyId={surveyId}
      sessionId={sessionId}
      questionId={questionId}
      questionText={questionText}
      maxImages={maxImages}
      onImagesChange={handleImagesChange}
      images={images}
      disabled={disabled}
      onTextFallback={handleTextFallbackChange}
      textFallback={textFallback}
    />
  );
};

export default PhotoUploadQuestion;
